import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import { getBackendError } from '../../../shared/utils/formErrors'
import { usePlatformAuth } from '../context/PlatformAuthContext'
import { PERM } from '../platformRoles'
import {
    ACCAO_LABEL,
    ESTADO_VALIDACAO_LABEL,
    ESTADO_VALIDACAO_TONE,
    formatData,
    formatDataHora,
    type EventoRegras,
    type Versao,
    type VersaoFormData,
} from '../regrasJuridicasModel'

type Decisao = 'reabrir' | 'revogar' | 'terminar'

/** Data nativa (aaaa-mm-dd): o painel da plataforma não tem o fornecedor de datas do resto da aplicação. */
function CampoData({ label, value, onChange, disabled, ajuda }: { label: string; value: string; onChange: (v: string) => void; disabled?: boolean; ajuda?: string }) {
    return (
        <TextField type="date" label={label} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} helperText={ajuda}
            slotProps={{ inputLabel: { shrink: true } }} />
    )
}

const VAZIO: VersaoFormData = { vigenciaInicio: '', vigenciaFim: '', fonteLegal: '', referenciaArtigos: '', notas: '', conteudo: '' }

const deVersao = (v: Versao): VersaoFormData => ({
    vigenciaInicio: v.vigenciaInicio,
    vigenciaFim: v.vigenciaFim ?? '',
    fonteLegal: v.fonteLegal ?? '',
    referenciaArtigos: v.referenciaArtigos ?? '',
    notas: v.notas ?? '',
    conteudo: v.conteudo,
})

const paraPedido = (f: VersaoFormData) => ({
    vigenciaInicio: f.vigenciaInicio,
    vigenciaFim: f.vigenciaFim || null,
    fonteLegal: f.fonteLegal.trim() || null,
    referenciaArtigos: f.referenciaArtigos.trim() || null,
    notas: f.notas.trim() || null,
    conteudo: f.conteudo,
})

/** O JSON do documento de regras, formatado para edição (se não for JSON válido, fica como está). */
const formatar = (texto: string) => {
    try { return JSON.stringify(JSON.parse(texto), null, 2) } catch { return texto }
}

/**
 * Uma versão de regras: edita-se enquanto é rascunho, submete-se, e outra pessoa valida-a. Depois de validada é
 * imutável — para mudar cria-se uma nova versão. As lacunas (folhas por definir) impedem a validação: nunca se
 * preenchem por omissão.
 */
export default function RegrasJuridicasVersao() {
    const { versaoId, jurisdicaoId } = useParams<{ versaoId?: string; jurisdicaoId?: string }>()
    const criar = !versaoId
    const navigate = useNavigate()
    const { notify } = useNotification()
    const { admin, hasPerm } = usePlatformAuth()
    const podeEditar = hasPerm(PERM.REGRAS_JURIDICAS_MANAGE)
    const podeValidar = hasPerm(PERM.REGRAS_JURIDICAS_VALIDAR)

    const [versao, setVersao] = useState<Versao | null>(null)
    const [eventos, setEventos] = useState<EventoRegras[]>([])
    const [form, setForm] = useState<VersaoFormData>(VAZIO)
    const [sujo, setSujo] = useState(false)
    const [isLoading, setIsLoading] = useState(!criar)
    const [aExecutar, setAExecutar] = useState(false)
    const [error, setError] = useState('')
    const [decisao, setDecisao] = useState<Decisao | null>(null)
    const [motivo, setMotivo] = useState('')
    const [fim, setFim] = useState('')

    const aplicar = useCallback((v: Versao) => {
        setVersao(v)
        setForm(deVersao({ ...v, conteudo: formatar(v.conteudo) }))
        setSujo(false)
    }, [])

    const carregar = useCallback(() => {
        if (!versaoId) return
        setIsLoading(true)
        setError('')
        Promise.all([
            api.get<Versao>(`/api/admin/regras-juridicas/versoes/${versaoId}`),
            api.get<EventoRegras[]>(`/api/admin/regras-juridicas/versoes/${versaoId}/eventos`),
        ])
            .then(([v, e]) => { aplicar(v.data); setEventos(e.data) })
            .catch(() => setError('Não foi possível carregar esta versão.'))
            .finally(() => setIsLoading(false))
    }, [versaoId, aplicar])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- carregar alterna a sua própria flag de loading
        carregar()
    }, [carregar])

    const set = <K extends keyof VersaoFormData>(campo: K, valor: VersaoFormData[K]) => {
        setForm((anterior) => ({ ...anterior, [campo]: valor }))
        setSujo(true)
    }

    const estado = versao?.estado
    const editavel = podeEditar && (criar || estado === 'RASCUNHO')
    const raiz = versao?.jurisdicaoId ?? jurisdicaoId

    /** Executa uma acção sobre a versão; todas devolvem a versão já actualizada. */
    const executar = async (pedido: () => Promise<{ data: Versao }>, sucesso: string, aoTerminar?: (v: Versao) => void) => {
        setAExecutar(true)
        setError('')
        try {
            const r = await pedido()
            aplicar(r.data)
            notify(sucesso)
            if (aoTerminar) aoTerminar(r.data)
            else if (versaoId) api.get<EventoRegras[]>(`/api/admin/regras-juridicas/versoes/${versaoId}/eventos`).then((e) => setEventos(e.data)).catch(() => undefined)
        } catch (err) {
            const corpo = getBackendError(err)
            setError(corpo?.fields ? Object.values(corpo.fields).join(' · ') : corpo?.message ?? 'Não foi possível concluir a acção.')
        } finally {
            setAExecutar(false)
        }
    }

    const guardar = () => {
        if (!form.vigenciaInicio) { setError('O início de vigência é obrigatório.'); return }
        if (!form.conteudo.trim()) { setError('O documento de regras é obrigatório.'); return }
        try { JSON.parse(form.conteudo) } catch { setError('O documento de regras não é JSON válido.'); return }
        if (criar) {
            void executar(() => api.post<Versao>(`/api/admin/regras-juridicas/jurisdicoes/${jurisdicaoId}/versoes`, paraPedido(form)),
                'Versão criada.', (v) => navigate(`/regras-juridicas/versoes/${v.id}`, { replace: true }))
        } else {
            void executar(() => api.put<Versao>(`/api/admin/regras-juridicas/versoes/${versaoId}`, paraPedido(form)), 'Versão guardada.')
        }
    }

    const usarModelo = async () => {
        if (form.conteudo.trim() && !window.confirm('Substituir o documento actual pelo modelo em branco?')) return
        try {
            const r = await api.get<{ conteudo: string }>('/api/admin/regras-juridicas/modelo')
            set('conteudo', formatar(r.data.conteudo))
        } catch {
            notify('Não foi possível obter o modelo.', 'error')
        }
    }

    const submeter = () => {
        if (sujo) { setError('Guarde as alterações antes de submeter para validação.'); return }
        if (!window.confirm('Submeter esta versão para validação? Deixa de se poder editar até ser reaberta.')) return
        void executar(() => api.post<Versao>(`/api/admin/regras-juridicas/versoes/${versaoId}/submeter`), 'Versão submetida para validação.')
    }

    const validar = () => {
        if (!versao) return
        const aviso = versao.editadaPorNome && versao.editadaPorNome === admin?.nome
            ? `Foi você quem editou esta versão. A validação deve ser feita por outra pessoa.\n\n` : ''
        if (!window.confirm(`${aviso}Validar a versão ${versao.numero}? Passa a ser aplicada às assembleias da sua vigência e deixa de se poder alterar.`)) return
        void executar(() => api.post<Versao>(`/api/admin/regras-juridicas/versoes/${versaoId}/validar`), 'Versão validada.')
    }

    const novaVersao = () => {
        if (!window.confirm('Criar uma nova versão (rascunho) a partir desta?')) return
        void executar(() => api.post<Versao>(`/api/admin/regras-juridicas/versoes/${versaoId}/nova-versao`), 'Nova versão criada.',
            (v) => navigate(`/regras-juridicas/versoes/${v.id}`))
    }

    const confirmarDecisao = () => {
        if (!decisao) return
        if (!motivo.trim()) { setError('Indique o motivo.'); return }
        const base = `/api/admin/regras-juridicas/versoes/${versaoId}`
        const fechar = () => { setDecisao(null); setMotivo(''); setFim('') }
        if (decisao === 'reabrir') void executar(() => api.post<Versao>(`${base}/reabrir`, { motivo }), 'Versão reaberta para edição.', fechar)
        else if (decisao === 'revogar') void executar(() => api.post<Versao>(`${base}/revogar`, { motivo }), 'Versão revogada.', fechar)
        else {
            if (!fim) { setError('Indique a data de fim.'); return }
            void executar(() => api.post<Versao>(`${base}/terminar-vigencia`, { fim, motivo }), 'Vigência terminada.', fechar)
        }
    }

    const tituloDecisao: Record<Decisao, string> = {
        reabrir: 'Reabrir para edição',
        revogar: 'Revogar a versão',
        terminar: 'Terminar a vigência',
    }

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
    }

    return (
        <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(raiz ? `/regras-juridicas/${raiz}` : '/regras-juridicas')} sx={{ mb: 2, color: 'text.secondary', pl: 0 }}>
                Versões
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {criar ? 'Nova versão de regras' : `${versao?.jurisdicaoCodigo} — versão ${versao?.numero}`}
                </Typography>
                {estado && (
                    <Chip size="small" label={ESTADO_VALIDACAO_LABEL[estado]} sx={{ fontWeight: 600, bgcolor: ESTADO_VALIDACAO_TONE[estado].bg, color: ESTADO_VALIDACAO_TONE[estado].color }} />
                )}
            </Box>
            {versao && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{versao.jurisdicaoNome}</Typography>}

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
                <AlertTitle>Não preencha por omissão</AlertTitle>
                Cada valor deve vir da fonte legal indicada. O que não estiver definido fica como lacuna — e uma versão com
                lacunas não se valida. Em caso de dúvida, deixe por definir.
            </Alert>

            {versao && !versao.completa && (
                <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                    <AlertTitle>{versao.lacunas.length} lacuna(s) por definir</AlertTitle>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 160, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {versao.lacunas.map((l) => <li key={l}>{l}</li>)}
                    </Box>
                </Alert>
            )}
            {versao?.completa && estado !== 'VALIDADA' && (
                <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>Documento completo: sem lacunas. Pode ser validado por quem tem esse poder.</Alert>
            )}
            {estado === 'REVOGADA' && versao?.motivoRevogacao && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>Revogada por {versao.revogadaPorNome} em {formatDataHora(versao.revogadaEm)}: {versao.motivoRevogacao}</Alert>
            )}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: { xs: 2.5, sm: 3 }, mb: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                    <CampoData label="Início de vigência" value={form.vigenciaInicio} onChange={(v) => set('vigenciaInicio', v)} disabled={!editavel} />
                    <CampoData label="Fim de vigência (opcional)" value={form.vigenciaFim} onChange={(v) => set('vigenciaFim', v)} disabled={!editavel} />
                    <TextField label="Fonte legal" value={form.fonteLegal} onChange={(e) => set('fonteLegal', e.target.value)} disabled={!editavel}
                        helperText="Diploma e data de publicação" />
                    <TextField label="Artigos" value={form.referenciaArtigos} onChange={(e) => set('referenciaArtigos', e.target.value)} disabled={!editavel}
                        helperText="Artigos de que decorrem as regras" />
                </Box>
                <TextField label="Notas" value={form.notas} onChange={(e) => set('notas', e.target.value)} disabled={!editavel} multiline minRows={2} fullWidth sx={{ mb: 2.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Documento de regras (JSON)</Typography>
                    {editavel && <Button size="small" onClick={usarModelo}>Partir do modelo em branco</Button>}
                </Box>
                <TextField
                    value={form.conteudo}
                    onChange={(e) => set('conteudo', e.target.value)}
                    disabled={!editavel}
                    multiline
                    minRows={16}
                    maxRows={36}
                    fullWidth
                    slotProps={{ htmlInput: { spellCheck: false, style: { fontFamily: 'monospace', fontSize: '0.8rem' } } }}
                />

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 3 }}>
                    {editavel && (
                        <Button variant="contained" onClick={guardar} disabled={aExecutar || (!criar && !sujo)}>
                            {aExecutar ? 'A guardar…' : criar ? 'Criar versão' : 'Guardar'}
                        </Button>
                    )}
                    {!criar && podeEditar && estado === 'RASCUNHO' && (
                        <Button variant="outlined" onClick={submeter} disabled={aExecutar}>Submeter para validação</Button>
                    )}
                    {!criar && podeEditar && estado === 'PENDENTE_VALIDACAO' && (
                        <Button variant="outlined" onClick={() => { setError(''); setDecisao('reabrir') }} disabled={aExecutar}>Reabrir para edição</Button>
                    )}
                    {!criar && podeValidar && estado === 'PENDENTE_VALIDACAO' && (
                        <Button variant="contained" color="success" onClick={validar} disabled={aExecutar || !versao?.completa}>Validar</Button>
                    )}
                    {!criar && podeValidar && (estado === 'PENDENTE_VALIDACAO' || estado === 'VALIDADA') && (
                        <Button variant="outlined" color="error" onClick={() => { setError(''); setDecisao('revogar') }} disabled={aExecutar}>Revogar</Button>
                    )}
                    {!criar && podeValidar && estado === 'VALIDADA' && (
                        <Button variant="outlined" onClick={() => { setError(''); setDecisao('terminar') }} disabled={aExecutar}>Terminar vigência</Button>
                    )}
                    {!criar && podeEditar && (estado === 'VALIDADA' || estado === 'REVOGADA') && (
                        <Button variant="outlined" onClick={novaVersao} disabled={aExecutar}>Nova versão a partir desta</Button>
                    )}
                </Box>
                {!criar && estado === 'PENDENTE_VALIDACAO' && !versao?.completa && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Com lacunas, a versão não pode ser validada: reabra-a e complete-a.
                    </Typography>
                )}
            </Paper>

            {versao && (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: { xs: 2.5, sm: 3 } }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Historial</Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Criada por {versao.criadaPorNome || '—'} em {formatDataHora(versao.createdAt)}
                        {versao.validadaPorNome ? ` · validada por ${versao.validadaPorNome} em ${formatDataHora(versao.validadaEm)}` : ''}
                        {versao.vigenciaFim ? ` · em vigor até ${formatData(versao.vigenciaFim)}` : ''}
                    </Typography>
                    {eventos.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Sem eventos registados.</Typography>
                    ) : (
                        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {eventos.map((e) => (
                                <Box component="li" key={e.id} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'baseline' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>{formatDataHora(e.criadoEm)}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{ACCAO_LABEL[e.accao] ?? e.accao}</Typography>
                                    <Typography variant="body2" color="text.secondary">{e.autorNome || 'sistema'}{e.motivo ? ` — ${e.motivo}` : ''}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Paper>
            )}

            <Dialog open={decisao !== null} onClose={() => !aExecutar && setDecisao(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: '1.05rem', fontWeight: 600 }}>{decisao ? tituloDecisao[decisao] : ''}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
                        {decisao === 'terminar' && (
                            <CampoData label="Última data em vigor" value={fim} onChange={setFim} />
                        )}
                        <TextField label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} multiline minRows={3} autoFocus required
                            helperText="Fica registado no historial da versão." />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDecisao(null)} disabled={aExecutar} color="inherit">Cancelar</Button>
                    <Button onClick={confirmarDecisao} variant="contained" color={decisao === 'revogar' ? 'error' : 'primary'} disabled={aExecutar}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
